import React, { useContext, useEffect, useRef } from 'react';
import { useCore } from './core';
import { usePlannerConfig } from './plannerConfig';
import { BehaviorKey, BehaviorParameters, BehaviorProps, Behaviors } from 'rangeConfig/@types';
import { useAppDispatch } from 'ducks/hooks';
import { addBreadcrumb } from '@sentry/react';
import * as R from 'ramda';
import { Product } from '@inter-ikea-storageone/range';
import { InteractionMeta, InteractionParams, useInteractionReporter } from 'analytics/interaction';
import { useSelector } from 'react-redux';
import { getRootState } from 'ducks/rootReducer';
import { useWidget } from './widget';
import { getConfigSorting } from 'ducks/products';

/**
 * Behavior System
 *
 * The behavior system is designed to handle user interactions in the various menus
 * and propagate the desired actions to core and the interaction tracker that reports
 * insights events. Common behavior trigger keys can be used in the rangeConfig
 * to specify desired handlers for these event types. Any non-common custom logic
 * defined in react components (widgets) should be wrapped in the behaviorSystem.custom
 * wrapper that makes sure events are also sent for these custom cases.
 */

type CustomBehaviorParameters = Omit<BehaviorParameters<BehaviorKey>, 'props'>;

export interface BehaviorContextProps {
  trigger: <K extends BehaviorKey>(
    triggerKey: K,
    ctx: Omit<InteractionParams, 'source'>,
    props: BehaviorProps<K>,
    groupBehaviors?: Behaviors
  ) => void;
  custom: (
    ctx: Omit<InteractionParams, 'source'>,
    fn: (p: CustomBehaviorParameters) => any,
    sendHistoryEvent?: boolean
  ) => any;
  startReport: ReturnType<typeof useInteractionReporter>['start'];
  endReport: (keyOrKeys: string | string[]) => void;
}

export const BehaviorContext = React.createContext<BehaviorContextProps>({
  trigger: () => {},
  custom: () => {},
  startReport: () => false,
  endReport: () => false
});

interface Props {
  children: React.ReactNode;
}

export const useBehaviorSystem = () => useContext(BehaviorContext);

export const useBehaviorTracker = () => {
  const behaviorSystem = useBehaviorSystem();
  const ctx = useWidget();
  const sorting = useSelector(getConfigSorting(ctx.configId));

  const context = useRef<InteractionParams>({ ...ctx, activeSorting: sorting?.id });
  const meta = useRef<InteractionMeta | undefined>(undefined);

  useEffect(() => {
    context.current = { ...context.current, activeSorting: sorting?.id };
  }, [sorting]);

  const trigger = <K extends BehaviorKey>(
    triggerKey: K,
    props: BehaviorProps<K>,
    groupBehaviors?: Behaviors
  ) =>
    behaviorSystem.trigger(
      triggerKey,
      { ...context.current, meta: meta.current },
      props,
      groupBehaviors
    );

  const custom = <F extends (p: CustomBehaviorParameters) => any>(
    fn: F,
    sendHistoryEvent = false
  ) => behaviorSystem.custom({ ...context.current, meta: meta.current }, fn, sendHistoryEvent);

  const startReport = (sessionId: string) => {
    behaviorSystem.startReport(sessionId, ctx);
  };

  const endReport = behaviorSystem.endReport;

  const withMeta = (metaValue: InteractionMeta) => {
    meta.current = metaValue;
    return tracker;
  };

  const withContext = (ctx: InteractionParams) => {
    context.current = { ...context.current, ...ctx };
    return tracker;
  };

  const tracker = {
    trigger,
    custom,
    withMeta,
    withContext,
    startReport,
    endReport
  };

  return tracker;
};

// Translate from new props format to old props which the behaviors still use
const translateProps = <K extends BehaviorKey>(key: K, newProps: BehaviorProps<K>) => {
  const props: any = {};

  if (key === 'productClicked' || key === 'productDropped') {
    const p = newProps as BehaviorProps<'productClicked' | 'productDropped'>;
    const product = p.targets[0] as Product | undefined;
    props.coreProductIds = p.targets.map(t => t.coreProductId);
    props.coreProductId = product?.coreProductId;
    props.globalItemNo = product?.globalItemNo;
  }

  if (key === 'productDropped')
    props.position = (newProps as BehaviorProps<'productDropped'>).position;

  return props;
};

export const BehaviorProvider = ({ children }: Props) => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const reporter = useInteractionReporter();
  const { plannerConfig } = usePlannerConfig();
  //ToDo remove usage of selector of the full state
  const rootState = useSelector(getRootState);

  const trigger = <K extends BehaviorKey>(
    triggerKey: K,
    ctx: Omit<InteractionParams, 'source'>,
    newProps: BehaviorProps<K>,
    groupBehaviors?: Behaviors
  ) => {
    const props = translateProps(triggerKey, newProps);

    addBreadcrumb({
      message: `Triggering behavior ${triggerKey} for group ${ctx.groupId}`,
      data: R.omit(['products'], props)
    });

    const group = plannerConfig.productGroups.find(group => group.id === ctx.groupId);

    const behaviors =
      groupBehaviors?.[triggerKey] ??
      group?.mainMenu.behaviors[triggerKey] ??
      group?.selectedMenu.behaviors[triggerKey] ??
      [];

    const runBehaviors = () => {
      if (!behaviors.length) return;

      const isSceneChange = [
        'productClicked',
        'productDropped',
        'productDeleted',
        'removeProducts'
      ].includes(triggerKey);

      const historyWrapper = isSceneChange ? coreApi.history.historyAction : (fn: any) => fn();

      historyWrapper(() =>
        behaviors.forEach(behavior => {
          try {
            behavior({ dispatch, coreApi, props, rootState, key: triggerKey });
          } catch (error) {
            coreApi.reportErrorToSentry(error as Error, { behavior: triggerKey, props });
          }
        })
      );
    };

    const context = Object.assign(ctx, {
      source: triggerKey,
      meta: { ...ctx.meta, targets: newProps?.targets }
    });

    reporter.wrap(context, runBehaviors);
  };

  const custom = <F extends (p: CustomBehaviorParameters) => any>(
    ctx: Omit<InteractionParams, 'source'>,
    fn: F,
    sendHistoryEvent = false
  ) =>
    reporter.wrap({ source: 'customBehavior', ...ctx }, () =>
      coreApi.history.historyAction(() => fn({ rootState, coreApi, dispatch }), sendHistoryEvent)
    );

  return (
    <BehaviorContext.Provider
      value={{ trigger, custom, startReport: reporter.start, endReport: reporter.end }}
    >
      {children}
    </BehaviorContext.Provider>
  );
};
